import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { PhotoRetakeIntro } from '@/components/survey/PhotoRetakeIntro'
import { useFullSurveyStore } from '@/store/fullSurveyStore'
import { useAuthStore } from '@/store/authStore'
import { useAnalysisStore } from '@/store/analysisStore'
import { useToastStore } from '@/store/toastStore'
import { trackFunnelEvent, trackEngagementEvent } from '@/services/analytics'
import { haptic } from '@/lib/haptics'
import {
  ETHNICITY_OPTIONS,
  ROUTINE_FREQUENCY_OPTIONS,
  SKIN_COLOR_OPTIONS,
  type Ethnicity,
  type RoutineFrequency,
  type SkinColor,
} from '@/types/fullSurvey'

// Decorative sparkle component (matching SurveyPage)
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Background gradients for each step (matching SurveyPage style)
const STEP_GRADIENTS = [
  'from-[#FFF0F5] via-[#FFFEF1] to-white', // Ethnicity - soft pink
  'from-[#F0F9FF] via-[#FFFEF1] to-white', // ZIP code - cool blue
  'from-[#F5FFF0] via-[#FFFEF1] to-white', // Routine frequency - fresh green
  'from-[#FFF8F0] via-[#FFFEF1] to-white', // Skin color - warm peach
]

const QUESTIONS = [
  {
    title: 'What is your ethnicity?',
    subtitle: 'This helps us provide more accurate skin recommendations',
  },
  {
    title: 'What is your ZIP code?',
    subtitle: 'We use this for weather-based skincare recommendations',
  },
  {
    title: 'How often do you follow a skincare routine?',
    subtitle: 'This helps us understand your current habits',
  },
  {
    title: 'What is your skin color?',
    subtitle: 'This helps us give personalized advice',
  },
]

export function FullSurveyPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    fullSurvey,
    currentStep,
    totalSteps,
    setEthnicity,
    setZipCode,
    setRoutineFrequency,
    setSkinColor,
    nextStep,
    prevStep,
    isCurrentStepValid,
    isComplete,
  } = useFullSurveyStore()
  const { isAuthenticated, hasPaid, demoMode } = useAuthStore()
  const { hasHDResults, analysisResults } = useAnalysisStore()
  const showToast = useToastStore((state) => state.showToast)

  // Check if user just returned from photo retake
  const locationState = location.state as { photoRetaken?: boolean } | null
  const justRetookPhoto = locationState?.photoRetaken === true

  // Show intro screen with photo preview and retake option
  // Skip intro if user just retook their photo - go directly to survey questions
  const [showIntroScreen, setShowIntroScreen] = useState(!justRetookPhoto)

  // Clear any navigation state to prevent issues on refresh
  useEffect(() => {
    if (location.state) {
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  // Route guard: check teaser flow completion first, then auth/payment
  const hasRedirected = useRef(false)
  useEffect(() => {
    if (hasRedirected.current) return

    // First check: must have completed teaser flow (has analysis results)
    if (!analysisResults) {
      hasRedirected.current = true
      navigate('/survey', { replace: true })
      return
    }

    // Second check: if already completed full analysis, go to report
    if (hasHDResults()) {
      hasRedirected.current = true
      navigate('/report', { state: { showWelcomeToast: true } })
      return
    }

    // Third check: must be authenticated and paid (unless demo mode)
    if (!demoMode && (!isAuthenticated || !hasPaid)) {
      hasRedirected.current = true
      navigate('/payment', { replace: true })
    }
  }, [analysisResults, hasHDResults, isAuthenticated, hasPaid, demoMode, navigate])

  // Custom back button handling:
  // - On intro screen: block back and show toast
  // - On survey step 0 (ethnicity): go back to intro screen (photo is part of survey)
  // - On survey step 1+: go to previous survey step
  useEffect(() => {
    const currentUrl = window.location.href
    let lastToastTime = 0

    // Push history entries to create buffer
    for (let i = 0; i < 10; i++) {
      window.history.pushState({ preventBack: true, index: i }, '', currentUrl)
    }

    const handlePopState = () => {
      // Restore history buffer
      for (let i = 0; i < 5; i++) {
        window.history.pushState({ preventBack: true, index: i }, '', currentUrl)
      }

      // Get current state from store
      const step = useFullSurveyStore.getState().currentStep

      if (showIntroScreen) {
        // On intro screen: block back, show toast
        const now = Date.now()
        if (now - lastToastTime > 2000) {
          lastToastTime = now
          showToast("Almost there! Complete the survey to see your full results.", 'warning')
          trackEngagementEvent.backPrevented('/full-survey')
        }
      } else if (step === 0) {
        // On first survey question (ethnicity): go back to photo retake intro
        setShowIntroScreen(true)
      } else {
        // On other survey steps: go to previous step
        useFullSurveyStore.getState().prevStep()
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [showIntroScreen, showToast])

  // Handle photo retake from intro screen
  const handlePhotoRetake = () => {
    haptic('light')
    navigate('/photo', { state: { returnTo: '/full-survey', isRetake: true } })
  }

  // Handle continue from intro screen (use current photo)
  const handleIntroScreenContinue = () => {
    haptic('light')
    setShowIntroScreen(false)
  }

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      nextStep()
    } else if (isComplete()) {
      trackFunnelEvent.fullSurveyCompleted({
        ethnicity: fullSurvey.ethnicity || 'unknown',
        zip_code: fullSurvey.zipCode || 'unknown',
      })
      navigate('/analysis-complete')
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      prevStep()
    } else {
      // On first question (ethnicity), go back to photo retake intro
      // This treats photo as part of the survey flow
      setShowIntroScreen(true)
    }
  }

  const currentQuestion = QUESTIONS[currentStep] ?? QUESTIONS[0]
  const safeStep = Math.max(0, Math.min(currentStep, STEP_GRADIENTS.length - 1))

  // Show intro screen with photo preview before survey questions
  if (showIntroScreen) {
    return (
      <PhotoRetakeIntro
        photoUrl={analysisResults?.photoUrl}
        onRetake={handlePhotoRetake}
        onContinue={handleIntroScreenContinue}
      />
    )
  }

  return (
    <div className={`h-dvh flex flex-col overflow-hidden bg-gradient-to-b ${STEP_GRADIENTS[safeStep]} relative`}>
      {/* Decorative sparkles */}
      <Sparkle className="absolute top-20 left-4 z-0" size={14} color="#FF8C33" delay={0} />
      <Sparkle className="absolute top-32 right-6 z-0" size={10} color="#F596FA" delay={0.5} />
      <Sparkle className="absolute bottom-32 left-8 z-0" size={12} color="#7182F8" delay={1} />

      {/* Fixed Header - Progress Bar */}
      <header className="shrink-0 bg-white/60 backdrop-blur-sm border-b border-gray-100 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <ProgressBar current={currentStep} total={totalSteps} />
        </div>
      </header>

      {/* Question + Options */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="flex-1 min-h-0 flex flex-col overflow-hidden relative z-10"
        >
          {/* Question Section */}
          <div className="shrink-0 px-4 pt-6 pb-4">
            <div className="max-w-md mx-auto text-center">
              <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl sm:text-3xl font-bold text-[#075D61] mb-2"
                style={{ fontFamily: "'Anybody', sans-serif" }}
              >
                {currentQuestion.title}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-gray-500"
              >
                {currentQuestion.subtitle}
              </motion.p>
            </div>
          </div>

          {/* Scrollable Options Section */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
            <div className="max-w-md mx-auto">
              {currentStep === 0 && (
                <EthnicitySelector
                  value={fullSurvey.ethnicity}
                  onChange={setEthnicity}
                />
              )}
              {currentStep === 1 && (
                <ZipCodeInput
                  value={fullSurvey.zipCode}
                  onChange={setZipCode}
                />
              )}
              {currentStep === 2 && (
                <RoutineFrequencySelector
                  value={fullSurvey.routineFrequency}
                  onChange={setRoutineFrequency}
                />
              )}
              {currentStep === 3 && (
                <SkinColorSelector
                  value={fullSurvey.skinColor}
                  onChange={setSkinColor}
                />
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Fixed Footer - Navigation Buttons */}
      <div className="shrink-0 bg-white/60 backdrop-blur-sm border-t border-gray-100 safe-bottom relative z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex gap-3">
            <Button variant="secondary" size="md" onClick={handleBack} className="w-24 shrink-0">
              Back
            </Button>
            <Button
              size="lg"
              onClick={handleNext}
              disabled={!isCurrentStepValid()}
              className="flex-1 whitespace-nowrap shadow-md"
            >
              {currentStep === totalSteps - 1 ? 'View Results' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Ethnicity selector component - no staggered animation to prevent flickering
function EthnicitySelector({
  value,
  onChange,
}: {
  value: Ethnicity | null
  onChange: (ethnicity: Ethnicity) => void
}) {
  return (
    <div className="space-y-3 pt-2">
      {ETHNICITY_OPTIONS.map((option) => {
        const isSelected = value === option.value
        return (
          <motion.button
            key={option.value}
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => {
              haptic('selection')
              onChange(option.value)
            }}
            className={`
              w-full p-4 rounded-2xl border-2 text-left transition-all duration-200
              active:scale-[0.98] transform shadow-sm
              ${
                isSelected
                  ? 'border-[#46C0C6] bg-[#ECF9F9] shadow-md'
                  : 'border-gray-100 hover:border-[#46C0C6]/50 hover:shadow-md bg-white'
              }
            `}
          >
            <div className="flex items-center gap-3">
              <motion.div
                animate={isSelected ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.2 }}
                className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all
                  ${isSelected ? 'border-[#46C0C6] bg-[#46C0C6]' : 'border-gray-300'}
                `}
              >
                {isSelected && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </motion.svg>
                )}
              </motion.div>
              <span className={`font-medium ${isSelected ? 'text-[#075D61]' : 'text-gray-900'}`}>
                {option.label}
              </span>
            </div>
          </motion.button>
        )
      })}
    </div>
  )
}

// ZIP code input component with animations
function ZipCodeInput({
  value,
  onChange,
}: {
  value: string | null
  onChange: (zipCode: string) => void
}) {
  const [inputValue, setInputValue] = useState(value ?? '')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-focus input on mount (delay for animation to complete)
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus()
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, limit to 5 for US ZIP codes
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 5)
    setInputValue(cleaned)
    onChange(cleaned)
  }

  const isValid = /^\d{5}$/.test(inputValue)
  const showError = inputValue.length > 0 && inputValue.length >= 5 && !isValid

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="pt-8 flex flex-col items-center"
    >
      <motion.input
        ref={inputRef}
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        type="text"
        inputMode="numeric"
        placeholder="Enter ZIP code"
        value={inputValue}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`
          w-full max-w-xs text-center text-2xl font-semibold py-4 px-6 rounded-2xl border-2 shadow-sm
          focus:outline-none transition-all
          ${
            showError
              ? 'border-red-400 bg-red-50'
              : isFocused || isValid
                ? 'border-[#46C0C6] bg-[#ECF9F9] shadow-md text-[#075D61]'
                : 'border-gray-200 bg-white'
          }
        `}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mt-4"
      >
        {showError ? (
          <p className="text-sm text-red-500 bg-red-50 px-4 py-2 rounded-full">
            Please enter a valid 5-digit ZIP code
          </p>
        ) : isValid ? (
          <motion.p
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="text-sm bg-[#ECF9F9] text-[#075D61] px-4 py-2 rounded-full font-medium"
          >
            <span className="text-[#46C0C6]">✓</span> Valid ZIP code
          </motion.p>
        ) : (
          <p className="text-sm text-gray-400">
            Example: 90210
          </p>
        )}
      </motion.div>

      {/* Info card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8 bg-gradient-to-br from-[#46C0C6]/10 to-[#46C0C6]/5 rounded-2xl p-4 w-full max-w-xs shadow-sm"
      >
        <div className="flex items-start gap-3">
          <span className="text-xl">🌤️</span>
          <div>
            <h4 className="font-medium text-[#075D61] text-sm">Why we need this</h4>
            <p className="text-xs text-gray-600 mt-1">
              Your local weather affects your skin. We'll customize product textures based on your
              climate.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Routine frequency selector component - matching EthnicitySelector style
function RoutineFrequencySelector({
  value,
  onChange,
}: {
  value: RoutineFrequency | null
  onChange: (frequency: RoutineFrequency) => void
}) {
  return (
    <div className="space-y-3 pt-2">
      {ROUTINE_FREQUENCY_OPTIONS.map((option) => {
        const isSelected = value === option.value
        return (
          <motion.button
            key={option.value}
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => {
              haptic('selection')
              onChange(option.value)
            }}
            className={`
              w-full p-4 rounded-2xl border-2 text-left transition-all duration-200
              active:scale-[0.98] transform shadow-sm
              ${
                isSelected
                  ? 'border-[#46C0C6] bg-[#ECF9F9] shadow-md'
                  : 'border-gray-100 hover:border-[#46C0C6]/50 hover:shadow-md bg-white'
              }
            `}
          >
            <div className="flex items-center gap-3">
              <motion.div
                animate={isSelected ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.2 }}
                className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all
                  ${isSelected ? 'border-[#46C0C6] bg-[#46C0C6]' : 'border-gray-300'}
                `}
              >
                {isSelected && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </motion.svg>
                )}
              </motion.div>
              <span className={`font-medium ${isSelected ? 'text-[#075D61]' : 'text-gray-900'}`}>
                {option.label}
              </span>
            </div>
          </motion.button>
        )
      })}
    </div>
  )
}

// Skin color selector component with color swatches
function SkinColorSelector({
  value,
  onChange,
}: {
  value: SkinColor | null
  onChange: (color: SkinColor) => void
}) {
  return (
    <div className="pt-4">
      <div className="grid grid-cols-4 gap-3">
        {SKIN_COLOR_OPTIONS.map((option, index) => {
          const isSelected = value === option.value
          return (
            <motion.button
              key={option.value}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.03 }}
              onClick={() => {
                haptic('selection')
                onChange(option.value)
              }}
              className={`
                flex items-center justify-center p-2 rounded-2xl border-2 transition-all duration-200
                active:scale-[0.95] transform
                ${
                  isSelected
                    ? 'border-[#46C0C6] bg-[#ECF9F9] shadow-md'
                    : 'border-gray-100 hover:border-[#46C0C6]/50 bg-white'
                }
              `}
            >
              <motion.div
                animate={isSelected ? { scale: [1, 1.15, 1] } : {}}
                transition={{ duration: 0.2 }}
                className={`
                  w-14 h-14 rounded-full shadow-inner border-2
                  ${isSelected ? 'border-[#46C0C6]' : 'border-gray-200'}
                `}
                style={{ backgroundColor: option.color }}
              />
            </motion.button>
          )
        })}
      </div>

      {/* Info note */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-6 bg-gradient-to-br from-[#46C0C6]/10 to-[#46C0C6]/5 rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <span className="text-xl">🎨</span>
          <div>
            <h4 className="font-medium text-[#075D61] text-sm">Why this matters</h4>
            <p className="text-xs text-gray-600 mt-1">
              Different skin tones respond differently to ingredients and treatments. This helps us personalize your recommendations.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
